import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bug, Lightbulb, CheckCircle2, AlertCircle, Camera, Upload, X, MessageSquarePlus } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { cn } from "../../lib/utils";
import { useFeedback } from "../../context/FeedbackContext";
import useAuth from "../../hooks/useAuth";
import { useIsMobile } from "../../hooks/use-mobile";
import useRetryFetch from "../../hooks/useRetryFetch";
import {
  submitFeedback,
  getFeedbackScreenshotUploadUrl,
  confirmFeedbackScreenshotUpload,
  type FeedbackType,
  type FeedbackScreenshot,
} from "../../api/feedback";
import { uploadImageToR2 } from "../../api/processImages";
import { resolveCurrentContextLabel, resolveFeatureArea } from "./resolveCurrentContextLabel";
import { getPreviousNavigation } from "../../utils/navigationHistory";
import { captureTabScreenshot } from "../../utils/captureScreenshot";

// Corner-docked widget, not a modal — same shape as the Clary design-chat
// bubble (designView/chat/DesignChatBubble.tsx): a round trigger button and
// an always-mounted panel toggled purely via scale/opacity, no backdrop, no
// Radix Dialog. Absorbs what used to be a separate FeedbackButton.tsx —
// mirrors DesignChatBubble owning both its trigger and its panel in one file.
const FeedbackWidget: React.FC = () => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const isMobile = useIsMobile();
  const { isOpen, openFeedback, closeFeedback } = useFeedback();
  const retryFetch = useRetryFetch();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [type, setType] = useState<FeedbackType>("bug");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [screenshot, setScreenshot] = useState<FeedbackScreenshot | null>(null);
  // Separate flags per attach path — sharing one meant clicking either
  // button visually spun both at once.
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [screenshotError, setScreenshotError] = useState("");
  // Collapsed to the trigger-button state for the moment of capture, then
  // restored — instantly (no transition), so the screenshot shows the real
  // page rather than our own panel, on every screen size, without
  // reintroducing the animated close/reopen glitch an earlier
  // transition-based version of this had.
  const [isMinimizedForCapture, setIsMinimizedForCapture] = useState(false);

  // Reset to a clean slate each time the panel opens, so a leftover
  // success/error state from a prior submission never bleeds into the next.
  useEffect(() => {
    if (isOpen) {
      setType("bug");
      setMessage("");
      setSubmitted(false);
      setErrorMsg("");
      setScreenshot(null);
      setScreenshotError("");
      setIsCapturing(false);
      setIsUploading(false);
    }
  }, [isOpen]);

  // Shared by both attach paths (auto-capture and manual file upload) — the
  // presigned-upload pipeline doesn't care how the image bytes were obtained.
  const uploadScreenshot = async (blob: Blob, mimeType: string, width?: number, height?: number) => {
    const { uploadUrl, key } = await getFeedbackScreenshotUploadUrl(retryFetch, mimeType);
    await uploadImageToR2(uploadUrl, blob as unknown as File, mimeType);
    const { image } = await confirmFeedbackScreenshotUpload(retryFetch, {
      key,
      size: blob.size,
      mimeType,
      width,
      height,
    });
    setScreenshot(image);
  };

  // Auto-capture: only works on desktop browsers — getDisplayMedia (real
  // screen/tab capture) has no mobile equivalent on any platform, so this
  // path is hidden on mobile entirely (see the "Attach a screenshot" button
  // below) rather than silently doing nothing when clicked.
  const handleAttachScreenshot = async () => {
    setScreenshotError("");
    setIsCapturing(true);
    // Minimized to the trigger-button state for the moment of capture only
    // (instantly, no transition) so the screenshot shows the actual page
    // rather than our own panel — on every screen size, not just mobile.
    setIsMinimizedForCapture(true);
    try {
      const captured = await captureTabScreenshot();

      if (!captured) {
        // User declined the browser's share prompt — not an error, just
        // leave the form as-is.
        return;
      }

      await uploadScreenshot(captured.blob, "image/png", captured.width, captured.height);
    } catch (error) {
      setScreenshotError(
        t("feedback_screenshot_error") ||
          "Couldn't capture a screenshot — you can still send feedback without one."
      );
    } finally {
      setIsCapturing(false);
      setIsMinimizedForCapture(false);
    }
  };

  // Manual upload: works everywhere, the only option on mobile since there's
  // no browser API for real screen capture there.
  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;

    setScreenshotError("");
    setIsUploading(true);
    try {
      const dimensions = await new Promise<{ width: number; height: number } | null>((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = URL.createObjectURL(file);
      });
      await uploadScreenshot(file, file.type, dimensions?.width, dimensions?.height);
    } catch (error) {
      setScreenshotError(
        t("feedback_screenshot_upload_error") ||
          "Couldn't upload that image — you can still send feedback without one."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const previous = getPreviousNavigation();
      const context = {
        route: `${window.location.pathname}${window.location.search}`,
        label: resolveCurrentContextLabel(location.pathname),
        userAgent: navigator.userAgent,
        previousRoute: previous?.route,
        previousLabel: previous?.label,
        screenshot: screenshot || undefined,
        featureArea: resolveFeatureArea(location.pathname),
      };

      const response = await submitFeedback(retryFetch, { type, message: message.trim(), context });

      if (response.type === "success") {
        setSubmitted(true);
        setTimeout(() => {
          closeFeedback();
        }, 1600);
      } else {
        setErrorMsg(response.message || t("feedback_error") || "Failed to send feedback. Please try again.");
      }
    } catch (error) {
      setErrorMsg(t("feedback_error") || "Failed to send feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!auth?.accessToken) return null;

  // MobileBottomNav is h-14 (56px) plus its own safe-area padding — clear it
  // exactly rather than guessing, and outrank its z-40 so stacking above it
  // doesn't depend on DOM order.
  const bottomOffset = isMobile ? "calc(56px + env(safe-area-inset-bottom, 0px) + 0.75rem)" : "1rem";

  return (
    <>
      {/* Panel — always mounted, hidden when closed or minimized (scale/opacity,
          not conditional render) so in-progress form state survives.
          Width: fills the viewport with margin on mobile (never exceeds it,
          unlike a fixed 360px which clips off-screen on narrow phones),
          fixed 360px from sm: up.
          z-[75]: a few full-viewport portaled overlays in this app (Rituals,
          GraduationSheet — home/cockpit/) render at z-[70] via
          createPortal(..., document.body), which otherwise fully hides this
          widget underneath them since it isn't part of their DOM subtree.
          Stays below the transient tooltip/menu layer (z-[9999]+). */}
      <div
        className={cn(
          "fixed end-4 start-4 sm:start-auto z-[75] w-auto sm:w-[360px] shadow-2xl rounded-2xl border overflow-hidden bg-background origin-bottom-right",
          isMinimizedForCapture ? "transition-none" : "transition-all duration-200",
          isOpen && !isMinimizedForCapture
            ? "scale-100 opacity-100 pointer-events-auto"
            : "scale-0 opacity-0 pointer-events-none"
        )}
        style={{ bottom: bottomOffset, maxHeight: "min(560px, calc(100vh - 160px))" }}
      >
        <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">{t("give_feedback") || "Give feedback"}</p>
            <p className="text-xs text-muted-foreground">
              {t("feedback_dialog_description") ||
                "Spotted a problem or have an idea? Tell us — we'll know exactly where you were."}
            </p>
          </div>
          <button
            type="button"
            onClick={closeFeedback}
            aria-label={t("close") || "Close"}
            className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-4" style={{ maxHeight: "calc(min(560px, 100vh - 160px) - 57px)" }}>
          {submitted ? (
            <div
              role="status"
              aria-live="polite"
              className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t("feedback_success") || "Thanks — your feedback has been received."}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setType("bug")}
                  disabled={isSubmitting}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium whitespace-nowrap transition-colors",
                    type === "bug"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input text-muted-foreground hover:bg-accent"
                  )}
                >
                  <Bug className="h-3.5 w-3.5 shrink-0" />
                  {t("report_a_problem") || "Report a problem"}
                </button>
                <button
                  type="button"
                  onClick={() => setType("suggestion")}
                  disabled={isSubmitting}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium whitespace-nowrap transition-colors",
                    type === "suggestion"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input text-muted-foreground hover:bg-accent"
                  )}
                >
                  <Lightbulb className="h-3.5 w-3.5 shrink-0" />
                  {t("suggest_something") || "Suggest something"}
                </button>
              </div>

              <Textarea
                placeholder={
                  type === "bug"
                    ? t("feedback_bug_placeholder") || "What went wrong?"
                    : t("feedback_suggestion_placeholder") || "What's your idea?"
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting}
                rows={4}
                className="resize-none"
                required
              />

              {screenshot ? (
                <div className="relative inline-block">
                  <img
                    src={screenshot.url}
                    alt={t("feedback_screenshot_alt") || "Attached screenshot"}
                    className="h-20 w-auto rounded-lg border object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setScreenshot(null)}
                    disabled={isSubmitting}
                    aria-label={t("remove_screenshot") || "Remove screenshot"}
                    title={t("remove_screenshot") || "Remove screenshot"}
                    className="absolute -top-2 -end-2 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow hover:opacity-90"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div>
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                    {t("feedback_screenshot_optional") || "Add a picture (optional)"}
                  </p>
                  <div
                    className={cn(
                      "grid gap-2",
                      // Auto-capture: desktop only — no mobile browser exposes
                      // a screen/tab-capture API, so offering this option
                      // there would just be a silent dead click.
                      isMobile ? "grid-cols-1" : "grid-cols-2"
                    )}
                  >
                    {!isMobile && (
                      <button
                        type="button"
                        onClick={handleAttachScreenshot}
                        disabled={isSubmitting || isCapturing || isUploading}
                        className="flex flex-col items-center gap-1 rounded-lg border border-dashed px-2 py-2.5 text-center transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-60"
                      >
                        {isCapturing ? (
                          <Spinner size="sm" />
                        ) : (
                          <Camera className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="text-xs font-medium leading-tight">
                          {isCapturing
                            ? t("capturing_screenshot") || "Capturing…"
                            : t("capture_current_screen") || "Capture this screen"}
                        </span>
                        {!isCapturing && (
                          <span className="text-[10px] leading-tight text-muted-foreground">
                            {t("capture_current_screen_hint") || "Auto-grabs what's on screen now"}
                          </span>
                        )}
                      </button>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleFileSelected}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting || isCapturing || isUploading}
                      className="flex flex-col items-center gap-1 rounded-lg border border-dashed px-2 py-2.5 text-center transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-60"
                    >
                      {isUploading ? (
                        <Spinner size="sm" />
                      ) : (
                        <Upload className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-xs font-medium leading-tight">
                        {isUploading
                          ? t("uploading_screenshot") || "Uploading…"
                          : t("upload_screenshot") || "Choose a photo"}
                      </span>
                      {!isUploading && (
                        <span className="text-[10px] leading-tight text-muted-foreground">
                          {t("upload_screenshot_hint") || "Pick an image from your device"}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {screenshotError && (
                <p className="text-xs text-muted-foreground">{screenshotError}</p>
              )}

              {errorMsg && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <Button type="submit" disabled={isSubmitting || !message.trim()} className="w-full h-10">
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Spinner size="sm" />
                    {t("sending") || "Sending…"}
                  </span>
                ) : (
                  t("send_feedback") || "Send"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* Trigger button — visible when the panel is closed */}
      <button
        type="button"
        onClick={openFeedback}
        aria-label={t("give_feedback") || "Give feedback"}
        title={t("give_feedback") || "Give feedback"}
        className={cn(
          "fixed end-4 z-[75] flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90",
          isMinimizedForCapture ? "transition-none" : "transition-all",
          isOpen || isMinimizedForCapture ? "scale-0 opacity-0 pointer-events-none" : "scale-100 opacity-100"
        )}
        style={{ bottom: bottomOffset }}
      >
        <MessageSquarePlus className="h-5 w-5" />
      </button>
    </>
  );
};

export default FeedbackWidget;
